// @ts-check
import React from 'react';

import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import { fromApiBoolean } from 'club-sauce';
import FullScreenError from 'components/molecules/FullScreenError';
import Actions from 'components/standard/HostingDetailsActions';
import Attributes from 'components/standard/HostingDetailsAttributes';
import Description from 'components/standard/HostingDetailsDescription';
import PageLayout from 'components/standard/PageLayout';
import TabBar from 'components/standard/TabBar';
import { IntroTarget, introTargets } from 'containers/introjs';
import { useLeftHandLayout } from 'containers/LeftHandLayout';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import { usePollAssetDetails } from 'modules/queries/asset/useDetails';
import useMonitoringBandwidthStats from 'modules/queries/monitoring/bandwidth/useStats';
import { usePollNetworkIpListPublic } from 'modules/queries/network/ip/useListPublic';
import NotFound from 'pages/error/NotFound';
import {
	Redirect,
	Link as RouterLink,
	useLocation,
	useParams,
} from 'react-router-dom';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';

/** @typedef {import('@liquidweb-dev/undercurrent').Theme['palette']} Palette */

/**
 * A layout for hosting details pages.
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
const HostingDetailsLayout = ({ children }) => {
	/** @type {{uniqId: string}} */
	const { uniqId } = useParams();
	const { pathname } = useLocation();
	const {
		data: assetDetailsData,
		isLoading: assetDetailsIsLoading,
		isError: assetDetailsIsError,
		error: assetDetailsError,
	} = usePollAssetDetails({
		uniq_id: uniqId,
		alsowith: hostingDetailsAlsowith,
	});

	const { data: publicIps } = usePollNetworkIpListPublic({
		uniq_id: uniqId,
		page_size: 9999,
		page_num: 1,
		expand_ips: 1,
	});

	const { isError: isBandwidthStatsError } = useMonitoringBandwidthStats({
		uniq_id: uniqId,
	});

	const { topInnerX } = useLeftHandLayout();

	if (
		assetDetailsIsError &&
		// @ts-expect-error
		assetDetailsError.statusCode === 404
	) {
		return <NotFound />;
	}

	if (assetDetailsIsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsIsError)} />
		);
	}

	const {
		assetType,
		canBackupsAndImages,
		canConsole,
		capabilities,
		custom_name: customName,
		custom_description: customDescription,
		disableConsole,
		domain,
		isBeyondHosting,
		isPrivateParent,
		isVmware,
		zone,
	} = assetDetailsData || {};

	if (isBeyondHosting) {
		return <Redirect to={`/servers/${uniqId}/beyond-hosting`} />;
	}

	if (isVmware) {
		return <Redirect to={`/servers/${uniqId}/vmware-vdc`} />;
	}

	if (isPrivateParent) {
		return <Redirect to={`/servers/${uniqId}/private-parent`} />;
	}

	const canNetworking =
		(zone?.id && fromApiBoolean(capabilities?.autoPrivateNetworking)) ||
		!isBandwidthStatsError ||
		capabilities?.provFirewall ||
		capabilities?.nocworxFirewall ||
		(publicIps && publicIps?.items.length > 0);

	const title = customName || domain || '';

	// the tabs correspond to the portion of the path after the server uniqId
	const tabsValue = pathname.split('/')[3] || 'overview';
	// prevent extra gap in PageLayout
	const shouldRenderDescription =
		Boolean(customDescription) || !(title === domain);

	return (
		<PageLayout
			title={title}
			attributesSlot={<Attributes />}
			actionsSlot={<Actions />}
			isLoading={assetDetailsIsLoading}
			description={
				shouldRenderDescription && (
					<Description
						serverDescription={customDescription}
						// don't show the domain if it's the same as the title
						domain={domain !== title ? domain : null}
					/>
				)
			}
		>
			<TabBar sx={{ marginBottom: 600 }} isLoading={assetDetailsIsLoading}>
				<Tabs value={tabsValue} variant="scrollable">
					<Tab
						component={RouterLink}
						value="overview"
						to={`/servers/${uniqId}`}
						label="Overview"
					/>
					<Tab
						component={RouterLink}
						value="monitoring"
						to={`/servers/${uniqId}/monitoring`}
						label="Monitoring"
					/>
					{canNetworking && (
						<Tab
							component={RouterLink}
							value="networking"
							to={`/servers/${uniqId}/networking`}
							label="Networking"
						/>
					)}
					{canBackupsAndImages && (
						<Tab
							component={RouterLink}
							value="backups"
							to={`/servers/${uniqId}/backups`}
							label={assetType === 'dedicated' ? 'Backups' : 'Backups & Images'}
						/>
					)}
					<Tab
						component={RouterLink}
						value="activity"
						to={`/servers/${uniqId}/activity`}
						label="Activity"
					/>
					{canConsole && !disableConsole && (
						<Tab
							component={RouterLink}
							value="console"
							to={`/servers/${uniqId}/console`}
							label="Console"
						/>
					)}
					<Tab
						component={RouterLink}
						value="settings"
						to={`/servers/${uniqId}/settings`}
						label={
							<IntroTarget introId={introTargets.serverInteriorSettingsTab}>
								<span>Settings</span>
							</IntroTarget>
						}
					/>
				</Tabs>
			</TabBar>
			<Stack sx={{ paddingX: { xs: `${topInnerX}px`, md: 0 }, flexGrow: 1 }}>
				{children}
			</Stack>
		</PageLayout>
	);
};

export default HostingDetailsLayout;
